/**
 * T.10 §9.7. How a resume and the portfolio word each education level.
 * D16: some college reads as what was studied, never as a missing degree.
 * Level ids are `api.educationlevel`. Shared by the wizard and the subgraph's
 * resumeStarter, so the board, the resume and the server word a level alike.
 */

export interface EducationFacts {
  levelId: number;
  /** The stored degree title, else the CIP title. */
  program?: string | null;
  schoolName?: string | null;
  finishYear?: number | null;
  finishExpected?: boolean | null;
  /** False for a program still in progress. */
  completed?: boolean | null;
}

/** A program named "Other", or ending ", Other", is left out (program name rules). */
export function cleanProgram(program: string | null | undefined): string {
  const p = (program ?? '').trim();
  if (!p || /^other$/i.test(p) || /,\s*other$/i.test(p)) {
    return '';
  }
  return p;
}

type Wording = (program: string) => string;

const withProgram =
  (lead: string): Wording =>
  (p) =>
    p ? `${lead}, ${p}` : lead;

const LEVELS: Record<number, Wording | null> = {
  1: null,
  2: () => 'High school diploma or equivalent',
  4: (p) => (p ? `College coursework in ${p}` : 'College coursework'),
  13: (p) =>
    p ? `Community College of the Air Force, coursework in ${p}` : 'Community College of the Air Force, coursework',
  3: withProgram('Certificate'),
  5: withProgram('Associate degree'),
  14: (p) =>
    p ? `Associate degree, ${p}, Community College of the Air Force` : 'Associate degree, Community College of the Air Force',
  6: withProgram("Bachelor's degree"),
  7: withProgram('Graduate certificate'),
  15: (p) => (p ? `Graduate coursework in ${p}` : 'Graduate coursework'),
  8: withProgram("Master's degree"),
  9: withProgram("Post-master's certificate"),
  10: withProgram('Professional degree'),
  11: withProgram('Doctoral degree'),
  12: withProgram('Postdoctoral training')
};

/** The short level names the board chip leads with. */
const CHIP_LEVEL: Record<number, string> = {
  1: 'Courses',
  2: 'High school diploma or equivalent',
  3: 'Certificate',
  4: 'Some college',
  5: 'Associate degree',
  6: "Bachelor's degree",
  7: 'Graduate certificate',
  8: "Master's degree",
  9: "Post-master's certificate",
  10: 'Professional degree',
  11: 'Doctoral degree',
  12: 'Postdoctoral training',
  13: 'Some CCAF courses',
  14: 'CCAF degree',
  15: "Some master's courses"
};

/**
 * The resume line, or null when the level prints no line (less than high
 * school: programs and courses only). Then ", school" and ", year";
 * in progress adds "(in progress)" and "Expected year".
 */
export function educationLine(facts: EducationFacts): string | null {
  const wording = LEVELS[facts.levelId];
  if (!wording) {
    return null;
  }
  const program = cleanProgram(facts.program);
  let line = wording(program);
  if (facts.schoolName?.trim()) {
    line += `, ${facts.schoolName.trim()}`;
  }
  const inProgress = facts.completed === false;
  if (facts.finishYear) {
    line += inProgress && facts.finishExpected ? `, Expected ${facts.finishYear}` : `, ${facts.finishYear}`;
  }
  if (inProgress) {
    line += ' (in progress)';
  }
  return line;
}

/** "Some college: Information Technology", for the What we know chip. */
export function educationChipLabel(facts: EducationFacts): string {
  const lead = CHIP_LEVEL[facts.levelId] ?? 'Education';
  const program = cleanProgram(facts.program);
  return program ? `${lead}: ${program}` : lead;
}
